import { msalInstance } from './index';
import { loginRequest } from './authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { openDB } from 'idb';

const UPLOAD_DB_NAME = 'attachment-queue-db';
const UPLOAD_STORE_NAME = 'queued-uploads';
const uploadDbPromise = openDB(UPLOAD_DB_NAME, 1, {
  upgrade(db) { db.createObjectStore(UPLOAD_STORE_NAME); },
});
const uploadIdbStore = {
  async get(key) { return (await uploadDbPromise).get(UPLOAD_STORE_NAME, key); },
  async set(key, val) { return (await uploadDbPromise).put(UPLOAD_STORE_NAME, val, key); },
  async del(key) { return (await uploadDbPromise).delete(UPLOAD_STORE_NAME, key); },
  async keys() { return (await uploadDbPromise).getAllKeys(UPLOAD_STORE_NAME); },
};

const API_DB_NAME = 'api-request-queue-db';
const API_STORE_NAME = 'queued-api-requests';
const apiDbPromise = openDB(API_DB_NAME, 1, {
    upgrade(db) { db.createObjectStore(API_STORE_NAME, { autoIncrement: true }); },
});
const apiQueueIdbStore = {
    async set(val) { return (await apiDbPromise).put(API_STORE_NAME, val); },
};

const HEADSHOT_DB_NAME = 'headshot-queue-db';
const HEADSHOT_STORE_NAME = 'queued-headshots';
const headshotDbPromise = openDB(HEADSHOT_DB_NAME, 1, {
  upgrade(db) { db.createObjectStore(HEADSHOT_STORE_NAME); },
});
const headshotIdbStore = {
  async set(key, val) { return (await headshotDbPromise).put(HEADSHOT_STORE_NAME, val, key); },
};

const getApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://localhost:7089';
    return `${baseUrl}${endpoint}`;
};

const getActiveToken = async () => {
    const account = msalInstance.getActiveAccount();
    if (account) {
        try {
            const response = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: account,
            });
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                console.warn("Silent token acquisition failed. An interaction is required.");
            }
            throw error;
        }
    }
    const localToken = sessionStorage.getItem('localUserToken');
    if (localToken) return localToken;
    throw new Error("User is not authenticated.");
};

export const getValidToken = () => getActiveToken();

const authenticatedFetch = async (endpoint, options = {}) => {
    const url = getApiUrl(endpoint);
    try {
        const token = await getActiveToken();
        const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers };
        const config = { ...options, headers };
        
        const response = await fetch(url, config);

        if (!response.ok) {
            const errorText = await response.text();
            try {
                const errorData = JSON.parse(errorText);
                throw new Error(errorData.message || errorText);
            } catch (e) {
                throw new Error(errorText || `API call to ${endpoint} failed with status: ${response.status}`);
            }
        }
        const responseText = await response.text();
        return responseText ? JSON.parse(responseText) : null;

    } catch (error) {
        if (options.method && options.method !== 'GET' && error instanceof TypeError && 'serviceWorker' in navigator && 'SyncManager' in window) {
            console.log(`Network failed for ${options.method} request. Queuing for background sync.`);
            const registration = await navigator.serviceWorker.ready;
            const requestToQueue = {
                url: url,
                method: options.method,
                body: options.body ? JSON.parse(options.body) : null,
                headers: { 'Content-Type': 'application/json' }
            };
            await apiQueueIdbStore.set(requestToQueue);
            await registration.sync.register('sync-api-requests');
            return { queued: true, message: "Your change has been saved and will sync when you're back online." };
        } else {
            console.error(`API request to ${endpoint} failed:`, error);
            throw error;
        }
    }
};

export const loginWithPassword = async (employeeNumber, password) => {
    try {
        const response = await fetch(getApiUrl('/api/auth/login'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ employeeNumber: parseInt(employeeNumber), password })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Login failed');
        }
        const { token } = await response.json();
        sessionStorage.setItem('localUserToken', token);
        return token;
    } catch (error) {
        console.error("Login failed:", error);
        throw error;
    }
};

export const localLogout = () => sessionStorage.removeItem('localUserToken');

export const uploadAttachment = async (employeeCertificationId, file) => {
    const token = await getActiveToken();
    const url = getApiUrl(`/api/skills/certification/${employeeCertificationId}/attachment`);
    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
        });

        if (response.status !== 202) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Attachment upload failed');
        }
        return response.json();

    } catch (error) {
        if (error instanceof TypeError && 'serviceWorker' in navigator && 'SyncManager' in window) {
            console.log('Network failed. Queuing upload for background sync.');
            const registration = await navigator.serviceWorker.ready;
            const uniqueId = `queued_upload_${new Date().getTime()}`;
            await uploadIdbStore.set(uniqueId, { url: url, file: file });
            await registration.sync.register('sync-new-attachments');
            return { queued: true, message: "Upload queued and will complete when connection is restored." };
        } else {
            throw error;
        }
    }
};

export const downloadAttachment = async (attachmentId, fileName) => {
    const token = await getActiveToken();
    const url = getApiUrl(`/api/skills/attachment/${attachmentId}/download`);
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);

    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('Download failed.');

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
};

export const getHeadshotUrl = async (userId) => {
    const token = await getActiveToken();
    const url = getApiUrl(`/api/skills/employee/${userId}/headshot`);
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);

    try {
        const response = await fetch(url, { headers });
        if (!response.ok) return null;

        const blob = await response.blob();
        return window.URL.createObjectURL(blob);
    } catch (error) {
        console.error("Failed to fetch headshot:", error);
        return null;
    }
};

export const uploadHeadshot = async (userId, imageBlob) => {
    const token = await getActiveToken();
    const url = getApiUrl(`/api/skills/employee/${userId}/headshot`);
    const formData = new FormData();
    formData.append('file', imageBlob, 'headshot.jpg');

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Headshot upload failed');
        }
        return response.json();

    } catch (error) {
        if (error instanceof TypeError && 'serviceWorker' in navigator && 'SyncManager' in window) {
            console.log('Network failed. Queuing headshot for background sync.');
            const registration = await navigator.serviceWorker.ready;
            await headshotIdbStore.set(userId, { userId: userId, imageBlob: imageBlob });
            await registration.sync.register('sync-new-headshots');
            return { queued: true, message: "Your new headshot has been saved and will upload when you're back online." };
        } else {
            throw error;
        }
    }
};

export const deleteHeadshot = (userId) => {
    return authenticatedFetch(`/api/skills/employee/${userId}/headshot`, { method: 'DELETE' });
};

export const getLevels = () => authenticatedFetch('/api/skills/levels');
export const getAllEmployees = () => authenticatedFetch('/api/skills/employees/all');
export const getEmployeesByLevel = (titleYearId) => authenticatedFetch(`/api/skills/employees?titleYearId=${titleYearId}`);
export const getEmployeeDetails = (employeeId) => authenticatedFetch(`/api/skills/employee/${employeeId}`);
export const updateEmployeeSkill = (dto) => authenticatedFetch('/api/skills/employeeskill', { method: 'POST', body: JSON.stringify(dto) });
export const updateMeeting = (dto) => authenticatedFetch('/api/skills/meeting', { method: 'POST', body: JSON.stringify(dto) });
export const addOrUpdateEmployeeCertification = (dto) => authenticatedFetch('/api/skills/employeecertification', { method: 'POST', body: JSON.stringify(dto) });
export const deleteEmployeeCertification = (certId) => authenticatedFetch(`/api/skills/employeecertification/${certId}`, { method: 'DELETE' });

export const addEmployeeComment = (dto) => authenticatedFetch('/api/skills/comment', { method: 'POST', body: JSON.stringify(dto) });
export const updateEmployeeComment = (commentId, content) => authenticatedFetch(`/api/skills/comment/${commentId}`, { method: 'PUT', body: JSON.stringify({ content }) });
export const deleteEmployeeComment = (commentId) => authenticatedFetch(`/api/skills/comment/${commentId}`, { method: 'DELETE' });
export const getMasterSkills = () => authenticatedFetch('/api/skills/skills');
export const getMasterCertifications = () => authenticatedFetch('/api/skills/certifications');
export const getSkillCategories = () => authenticatedFetch('/api/skills/skillcategories');
export const getTitleYears = () => authenticatedFetch('/api/skills/titleyears');
export const getRequiredCertsForTitle = (titleYearId) => authenticatedFetch(`/api/skills/requiredcerts/${titleYearId}`);
export const getCurrentUserProfile = () => authenticatedFetch('/api/users/me');
export const getUsers = () => authenticatedFetch('/api/admin/users');
export const getAdminTitleYears = () => authenticatedFetch('/api/admin/titleyears');
export const createUser = (userRequest) => authenticatedFetch('/api/admin/user', { method: 'POST', body: JSON.stringify(userRequest) });
export const updateUser = (userId, userDto) => authenticatedFetch(`/api/admin/user/${userId}`, { method: 'PUT', body: JSON.stringify(userDto) });
export const convertUserToLocal = (userId, password) => authenticatedFetch(`/api/admin/user/${userId}/convert-to-local`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
export const convertUserToSso = (userId, email) => authenticatedFetch(`/api/admin/user/${userId}/convert-to-sso`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
export const resetUserPassword = (userId, newPassword) => authenticatedFetch(`/api/admin/user/${userId}/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ newPassword }) });
export const createMasterSkill = (skill) => authenticatedFetch('/api/admin/skill', { method: 'POST', body: JSON.stringify(skill) });
export const createSkillCategory = (category) => authenticatedFetch('/api/admin/skillcategory', { method: 'POST', body: JSON.stringify(category) });
export const createMasterCertification = (certification) => authenticatedFetch('/api/admin/certification', { method: 'POST', body: JSON.stringify(certification) });
export const updateMasterSkill = (skillId, skill) => authenticatedFetch(`/api/admin/skill/${skillId}`, { method: 'PUT', body: JSON.stringify(skill) });
export const updateSkillCategory = (categoryId, category) => authenticatedFetch(`/api/admin/skillcategory/${categoryId}`, { method: 'PUT', body: JSON.stringify(category) });
export const updateMasterCertification = (certId, certification) => authenticatedFetch(`/api/admin/certification/${certId}`, { method: 'PUT', body: JSON.stringify(certification) });
export const getRequiredCerts = () => authenticatedFetch('/api/admin/requiredcerts');
export const createRequiredCert = (requiredCert) => authenticatedFetch('/api/admin/requiredcert', { method: 'POST', body: JSON.stringify(requiredCert) });
export const deleteRequiredCert = (requiredCertId) => authenticatedFetch(`/api/admin/requiredcert/${requiredCertId}`, { method: 'DELETE' });
export const createTitleYear = (titleYear) => authenticatedFetch('/api/admin/titleyear', { method: 'POST', body: JSON.stringify(titleYear) });
export const updateTitleYear = (id, titleYear) => authenticatedFetch(`/api/admin/titleyear/${id}`, { method: 'PUT', body: JSON.stringify(titleYear) });

export const getGeneralMasterData = async () => {
    const [skills, categories, certifications, titleYears] = await Promise.all([ getMasterSkills(), getSkillCategories(), getMasterCertifications(), getTitleYears() ]);
    return { skillsMasterList: skills || [], skillCategories: categories || [], possibleCerts: certifications || [], titleYears: titleYears || [] };
};
export const getAdminMasterData = async () => {
    const [skills, categories, certifications, titleYears, requiredCerts] = await Promise.all([ getMasterSkills(), getSkillCategories(), getMasterCertifications(), getAdminTitleYears(), getRequiredCerts() ]);
    const generalData = { skillsMasterList: skills || [], skillCategories: categories || [], possibleCerts: certifications || [], titleYears: titleYears || [] };
    const requiredCertsByTitleYearId = {};
    if (generalData.possibleCerts && requiredCerts) {
        requiredCerts.forEach(rc => {
            const certName = generalData.possibleCerts.find(c => c.id === rc.certificationId)?.name;
            if (certName) {
                if (!requiredCertsByTitleYearId[certName]) requiredCertsByTitleYearId[certName] = [];
                requiredCertsByTitleYearId[certName].push(rc.titleYearId);
            }
        });
    }
    return { ...generalData, adminSettings: { requiredCertsByTitleYearId } };
};